import React, { useEffect, useState } from 'react';
import { Accordion, Button, Checkbox, Dropdown, Form, Icon, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import {
  DEFAULT_PAYLOAD_LOG_DAYS,
  EXPIRY_PRESETS,
  PAYLOAD_LOG_DAYS,
  amountToQuota,
  expiryForPreset,
  fromLocalInputValue,
  payloadGoesToArchive,
  presetForExpiry,
  quotaToAmount,
  toLocalInputValue,
} from './token-form';
import './TokenModal.css';

const DEFAULT_QUOTA = 500000;

const quotaUnit = () => {
  if (localStorage.getItem('display_in_currency') !== 'true') return 1;
  const unit = parseFloat(localStorage.getItem('quota_per_unit') || '1');
  return unit > 0 ? unit : 1;
};

const nowSec = () => Math.floor(Date.now() / 1000);

const emptyForm = (unit) => ({
  name: '',
  unlimited: false,
  amount: quotaToAmount(DEFAULT_QUOTA, unit),
  preset: 'never',
  custom: toLocalInputValue(nowSec() + 30 * 86400),
  models: [],
  subnet: '',
  createdTime: null,
  remainQuota: DEFAULT_QUOTA,
  logDays: DEFAULT_PAYLOAD_LOG_DAYS,
});

const TokenModal = ({ open, tokenId, onClose }) => {
  const { t } = useTranslation();
  const isEdit = tokenId !== undefined && tokenId !== null;
  const unit = quotaUnit();
  const [form, setForm] = useState(() => emptyForm(unit));
  const [modelOptions, setModelOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(emptyForm(unit));
    setShowAdvanced(false);
    API.get('/api/user/available_models')
      .then((res) => {
        const { success, data } = (res && res.data) || {};
        if (success && Array.isArray(data)) {
          setModelOptions(data.map((m) => ({ key: m, text: m, value: m })));
        }
      })
      .catch(() => {});
    if (!isEdit) return;
    setLoading(true);
    API.get(`/api/token/${tokenId}`)
      .then((res) => {
        const { success, message, data } = (res && res.data) || {};
        if (!success || !data) {
          showError(message);
          return;
        }
        const models = data.models ? data.models.split(',').filter(Boolean) : [];
        const preset = presetForExpiry(data.expired_time);
        set({
          name: data.name || '',
          unlimited: !!data.unlimited_quota,
          amount: quotaToAmount(data.remain_quota, unit),
          remainQuota: data.remain_quota,
          preset,
          custom:
            preset === 'custom'
              ? toLocalInputValue(data.expired_time)
              : toLocalInputValue(nowSec() + 30 * 86400),
          models,
          subnet: data.subnet || '',
          createdTime: data.created_time || null,
          logDays: data.payload_log_days || 0,
        });
        setShowAdvanced(models.length > 0 || !!data.subnet);
      })
      .finally(() => setLoading(false));
  }, [open, tokenId]);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('token.form.name_required');
    if (!form.unlimited && amountToQuota(form.amount, unit) === null) {
      next.amount = t('token.form.invalid_amount');
    }
    if (form.preset === 'custom') {
      const sec = fromLocalInputValue(form.custom);
      if (sec === null || sec <= nowSec()) next.custom = t('token.form.invalid_expiry');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    const expired =
      form.preset === 'custom'
        ? fromLocalInputValue(form.custom)
        : expiryForPreset(form.preset, nowSec());
    const payload = {
      name: form.name.trim(),
      unlimited_quota: form.unlimited,
      remain_quota: form.unlimited ? Number(form.remainQuota) || 0 : amountToQuota(form.amount, unit),
      expired_time: expired,
      models: form.models.join(','),
      subnet: form.subnet.trim(),
      payload_log_days: form.logDays,
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await API.put('/api/token/', { ...payload, id: parseInt(tokenId, 10) })
        : await API.post('/api/token/', payload);
      const { success, message } = (res && res.data) || {};
      if (success) {
        showSuccess(
          isEdit ? t('token.edit.messages.update_success') : t('token.edit.messages.create_success')
        );
        onClose(true);
      } else {
        showError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const currency = unit > 1;

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
      size='small'
      className='pz-token-modal'
      closeIcon
    >
      <Modal.Header>
        {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
      </Modal.Header>
      <Modal.Content scrolling>
        {isEdit && form.createdTime && (
          <p className='pz-help pz-created'>
            {t('token.form.created_at', { time: timestamp2string(form.createdTime) })}
          </p>
        )}
        <Form loading={loading} autoComplete='off' onSubmit={submit}>
          <Form.Input
            label={t('token.edit.name')}
            placeholder={t('token.form.name_placeholder')}
            value={form.name}
            onChange={(_, { value }) => set({ name: value })}
            error={errors.name ? { content: errors.name, pointing: 'above' } : null}
            required
            autoFocus
          />

          <div className='field'>
            <label>{t('token.form.quota')}</label>
            <div className='pz-segment' role='radiogroup'>
              {[false, true].map((unl) => (
                <button
                  type='button'
                  key={String(unl)}
                  role='radio'
                  aria-checked={form.unlimited === unl}
                  className={form.unlimited === unl ? 'active' : ''}
                  onClick={() => set({ unlimited: unl })}
                >
                  {unl ? t('token.form.unlimited') : t('token.form.limited')}
                </button>
              ))}
            </div>
          </div>
          {!form.unlimited && (
            <Form.Input
              label={t('token.form.amount')}
              type='number'
              min='0'
              step={currency ? '0.01' : '1'}
              inputMode='decimal'
              icon={currency ? 'dollar sign' : null}
              iconPosition={currency ? 'left' : undefined}
              value={form.amount}
              onChange={(_, { value }) => set({ amount: value })}
              error={errors.amount ? { content: errors.amount, pointing: 'above' } : null}
            />
          )}
          <p className='pz-help'>{t('token.form.quota_help')}</p>

          <div className='field'>
            <label>{t('token.form.expiry')}</label>
            <div className='pz-chips'>
              {EXPIRY_PRESETS.map((p) => (
                <button
                  type='button'
                  key={p}
                  className={form.preset === p ? 'active' : ''}
                  aria-pressed={form.preset === p}
                  onClick={() => set({ preset: p })}
                >
                  {t('token.form.presets.' + p)}
                </button>
              ))}
            </div>
          </div>
          {form.preset === 'custom' && (
            <Form.Input
              type='datetime-local'
              value={form.custom}
              onChange={(_, { value }) => set({ custom: value })}
              error={errors.custom ? { content: errors.custom, pointing: 'above' } : null}
            />
          )}

          <div className='field pz-logging'>
            <div className='pz-logging-head'>
              <label htmlFor='pz-logging-toggle'>{t('token.form.logging')}</label>
              <Checkbox
                id='pz-logging-toggle'
                toggle
                checked={form.logDays !== 0}
                onChange={(_, { checked }) => set({ logDays: checked ? DEFAULT_PAYLOAD_LOG_DAYS : 0 })}
              />
            </div>
            {form.logDays !== 0 && (
              <>
                <div className='pz-chips'>
                  {PAYLOAD_LOG_DAYS.map((d) => (
                    <button
                      type='button'
                      key={d}
                      className={form.logDays === d ? 'active' : ''}
                      aria-pressed={form.logDays === d}
                      onClick={() => set({ logDays: d })}
                    >
                      {t('token.form.log_days.' + (d === -1 ? 'forever' : d))}
                    </button>
                  ))}
                </div>
                <p className='pz-help pz-logging-note'>
                  {t('token.form.logging_note')}
                  {payloadGoesToArchive(form.logDays) ? ' ' + t('token.form.logging_archive_note') : ''}
                </p>
              </>
            )}
          </div>

          <Accordion className='pz-advanced'>
            <Accordion.Title active={showAdvanced} onClick={() => setShowAdvanced((s) => !s)}>
              <Icon name='dropdown' />
              {t('token.form.advanced')}
            </Accordion.Title>
            <Accordion.Content active={showAdvanced}>
              <div className='field'>
                <label>{t('token.edit.models')}</label>
                <Dropdown
                  placeholder={t('token.form.models_placeholder')}
                  fluid
                  multiple
                  search
                  selection
                  options={modelOptions}
                  value={form.models}
                  onChange={(_, { value }) => set({ models: value })}
                />
                <p className='pz-help'>{t('token.form.models_help')}</p>
              </div>
              <Form.Input
                label={t('token.edit.ip_limit')}
                placeholder={t('token.form.ip_placeholder')}
                value={form.subnet}
                onChange={(_, { value }) => set({ subnet: value })}
              />
              <p className='pz-help'>{t('token.form.ip_help')}</p>
            </Accordion.Content>
          </Accordion>
          {/* lets Enter submit the form */}
          <button type='submit' hidden aria-hidden='true' tabIndex={-1} />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type='button' onClick={() => onClose(false)} disabled={saving}>
          {t('token.edit.buttons.cancel')}
        </Button>
        <Button type='button' primary onClick={submit} loading={saving} disabled={saving || loading}>
          {isEdit ? t('token.form.save') : t('token.form.create')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TokenModal;
